import { CopyOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Checkbox,
  Descriptions,
  Drawer,
  Input,
  message,
  Modal,
  Select,
  Segmented,
  Space,
  Spin,
  Table,
} from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../api';
import { getLunarDate } from '../../utils/lunar';
import { getHolidayName } from '../../utils/holiday';
import { getScheduleTimezone, setScheduleTimezone, SCHEDULE_TIMEZONE_OPTIONS } from '../../utils/scheduleTime';
import NotificationHistory from './NotificationHistory';
import { ScheduleFormModal } from './ScheduleFormModal';

const EVENT_BG_COLORS = [
  '#e6f7ff', '#f6ffed', '#fff7e6', '#fff1f0', '#f9f0ff', '#e6fffb',
  '#e0f2f1', '#fce4ec', '#f3e5f5', '#e8eaf6',
];

/** 左侧用户列表每人一种颜色（色块/边框），与日历事件色系一致 */
const USER_STRIP_COLORS = [
  '#1a73e8', '#ea4335', '#34a853', '#f9ab00', '#e8710a',
  '#9334e6', '#0b8043', '#7986cb', '#ad1457', '#00897b',
  '#5e35b1', '#43a047',
];

const getEventColor = (ev) => {
  const id = typeof ev.id === 'number' ? ev.id : (ev.course_name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return EVENT_BG_COLORS[Math.abs(id) % EVENT_BG_COLORS.length];
};

/** 按用户 id 取固定颜色，用于左侧列表色块 */
const getUserStripColor = (userId) => {
  const id = typeof userId === 'number' ? userId : (userId || 0);
  return USER_STRIP_COLORS[Math.abs(id) % USER_STRIP_COLORS.length];
};

const formatEventTime = (timeStr, tz) => {
  const time = tz ? dayjs(timeStr).tz(tz) : dayjs(timeStr);
  const hour = time.hour();
  const minute = time.minute();
  if (hour === 0) {
    return minute === 0 ? '12am' : `12:${minute.toString().padStart(2, '0')}am`;
  }
  if (hour < 12) {
    return minute === 0 ? `${hour}am` : `${hour}:${minute.toString().padStart(2, '0')}am`;
  }
  if (hour === 12) {
    return minute === 0 ? '12pm' : `12:${minute.toString().padStart(2, '0')}pm`;
  }
  const displayHour = hour - 12;
  return minute === 0 ? `${displayHour}pm` : `${displayHour}:${minute.toString().padStart(2, '0')}pm`;
};

const getEventDisplayName = (ev, currentPersonId) => {
  const participants = ev.participants || [];
  const otherParticipants = participants
    .filter((p) => p.user_id !== currentPersonId)
    .map((p) => p.user_real_name || p.user_name || '')
    .filter(Boolean);
  if (otherParticipants.length > 0) return otherParticipants[0];
  return ev.teacher_name || '';
};

const formatEventText = (ev, currentPersonId, tz) => {
  const time = formatEventTime(ev.start_time, tz);
  const participantName = getEventDisplayName(ev, currentPersonId);
  const courseName = ev.course_name || '';
  if (participantName) return `${time} ${participantName} ${courseName}`;
  return `${time} ${courseName}`;
};

/** 极简风格：上午/下午 X点 + 课程名，单行；可选 tz 为排课显示时区 */
const formatEventTimeChinese = (timeStr, tz) => {
  const time = tz ? dayjs(timeStr).tz(tz) : dayjs(timeStr);
  const hour = time.hour();
  const minute = time.minute();
  const suffix = minute === 0 ? '点' : `点${minute}分`;
  if (hour === 0) return `上午12${suffix}`;
  if (hour < 12) return `上午${hour}${suffix}`;
  if (hour === 12) return `下午12${suffix}`;
  return `下午${hour - 12}${suffix}`;
};

const formatEventTextMinimal = (ev, currentPersonId, tz) => {
  const timeStr = formatEventTimeChinese(ev.start_time, tz);
  const courseName = ev.course_name || getEventDisplayName(ev, currentPersonId) || '';
  return courseName ? `${timeStr} ${courseName}` : timeStr;
};

/** 日历事件圆点颜色：优先用勾选用户中与事件相关者的色条颜色 */
const getEventDotColor = (ev, selectedPersonIds) => {
  const ids = selectedPersonIds || [];
  if (ev.teacher_id && ids.includes(ev.teacher_id)) return getUserStripColor(ev.teacher_id);
  const participantIds = (ev.participants || []).map((p) => p.user_id);
  const firstMatch = ids.find((id) => participantIds.includes(id));
  if (firstMatch != null) return getUserStripColor(firstMatch);
  return getUserStripColor(ev.id);
};

const SIDEBAR_WIDTH = 200;

const ScheduleCalendarPage = () => {
  const navigate = useNavigate();
  const [role, setRole] = useState('teacher');
  const [searchText, setSearchText] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [contacts, setContacts] = useState({ count: 0, results: [] });
  const [contactsLoading, setContactsLoading] = useState(false);
  const [selectedPersonIds, setSelectedPersonIds] = useState([]);

  const [calendarYear, setCalendarYear] = useState(dayjs().year());
  const [calendarMonth, setCalendarMonth] = useState(dayjs().month() + 1);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' | 'list'
  const [scheduleTz, setScheduleTz] = useState(() => getScheduleTimezone());
  const formatInTz = useCallback(
    (isoString, fmt) => (isoString ? dayjs(isoString).tz(scheduleTz).format(fmt) : ''),
    [scheduleTz]
  );
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);

  const [viewingSchedule, setViewingSchedule] = useState(null);
  const [viewingScheduleLoading, setViewingScheduleLoading] = useState(false);
  const [createMeetLoading, setCreateMeetLoading] = useState(false);
  const [dayEventsModal, setDayEventsModal] = useState({ open: false, date: null, events: [] });
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const fetchContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      if (role === 'all') {
        const search = searchValue.trim() || undefined;
        const [tRes, sRes] = await Promise.all([
          axios.get('/api/schedules/contacts/', { params: { role: 'teacher', page: 1, page_size: 200, ...(search && { search }) } }),
          axios.get('/api/schedules/contacts/', { params: { role: 'student', page: 1, page_size: 200, ...(search && { search }) } }),
        ]);
        const tResults = tRes.data.results || [];
        const sResults = sRes.data.results || [];
        const merged = [...tResults, ...sResults].sort((a, b) => {
          const roleOrder = (r) => (r === 'teacher' ? 0 : 1);
          if (roleOrder(a.role) !== roleOrder(b.role)) return roleOrder(a.role) - roleOrder(b.role);
          return (a.real_name || a.username || '').localeCompare(b.real_name || b.username || '');
        });
        setContacts({ count: merged.length, results: merged });
      } else {
        const params = { role, page: 1, page_size: 200 };
        if (searchValue.trim()) params.search = searchValue.trim();
        const res = await axios.get('/api/schedules/contacts/', { params });
        setContacts({ count: res.data.count || 0, results: res.data.results || [] });
      }
    } catch (e) {
      message.error(e.response?.data?.detail || '加载联系人失败');
      setContacts({ count: 0, results: [] });
    } finally {
      setContactsLoading(false);
    }
  }, [role, searchValue]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    setSelectedPersonIds([]);
  }, [role]);

  const fetchCalendar = useCallback(async () => {
    if (selectedPersonIds.length === 0) {
      setCalendarEvents([]);
      return;
    }
    setCalendarLoading(true);
    try {
      const start = dayjs().year(calendarYear).month(calendarMonth - 1).date(1);
      const end = start.endOf('month');
      const params = {
        person_ids: selectedPersonIds.join(','),
        start_date: start.format('YYYY-MM-DD'),
        end_date: end.format('YYYY-MM-DD'),
      };
      const res = await axios.get('/api/schedules/calendar/', { params });
      setCalendarEvents(Array.isArray(res.data) ? res.data : []);
    } catch {
      setCalendarEvents([]);
    } finally {
      setCalendarLoading(false);
    }
  }, [selectedPersonIds, calendarYear, calendarMonth]);

  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  const displayName = (row) => row?.real_name || row?.username || row?.email || `#${row?.id}`;

  const onContactSelect = (record, checked) => {
    if (checked) {
      setSelectedPersonIds((prev) => (prev.includes(record.id) ? prev : [...prev, record.id]));
    } else {
      setSelectedPersonIds((prev) => prev.filter((id) => id !== record.id));
    }
  };

  const selectAllOnPage = () => {
    const ids = contacts.results.map((r) => r.id);
    setSelectedPersonIds((prev) => {
      const set = new Set([...prev, ...ids]);
      return Array.from(set);
    });
  };

  const clearSelection = () => setSelectedPersonIds([]);

  const deselectAllOnPage = () => {
    const pageIds = contacts.results.map((r) => r.id);
    setSelectedPersonIds((prev) => prev.filter((id) => !pageIds.includes(id)));
  };

  const openView = async (ev) => {
    setViewingSchedule({ _loading: true, id: ev.id, course_name: ev.course_name });
    setViewingScheduleLoading(true);
    try {
      const res = await axios.get(`/api/schedules/${ev.id}/`);
      setViewingSchedule(res.data);
    } catch (e) {
      message.error(e.response?.data?.detail || '加载排课详情失败');
      setViewingSchedule(null);
    } finally {
      setViewingScheduleLoading(false);
    }
  };

  const handleCreateMeet = async (scheduleId) => {
    setCreateMeetLoading(true);
    try {
      const res = await axios.post(`/api/schedules/${scheduleId}/create-meet/`);
      const meetLink = res.data?.meet_link;
      if (meetLink) {
        message.success('Meet 会议链接已创建');
        const detailRes = await axios.get(`/api/schedules/${scheduleId}/`);
        setViewingSchedule(detailRes.data);
      }
    } catch (e) {
      message.error(e.response?.data?.detail || '创建 Meet 链接失败');
    } finally {
      setCreateMeetLoading(false);
    }
  };

  const isRealMeetLink = (link) => link && (link.startsWith('http://') || link.startsWith('https://'));

  const copyLaunchUrl = async (scheduleId) => {
    try {
      const res = await axios.get(`/api/schedules/${scheduleId}/launch-url/`);
      const url = res.data?.launch_url || res.data?.url;
      if (url && navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        message.success('已复制到剪贴板');
      }
    } catch (e) {
      message.error(e.response?.data?.detail || '复制失败');
    }
  };

  const deleteSchedule = async (record) => {
    try {
      await axios.delete(`/api/schedules/${record.id}/`);
      message.success('已删除');
      setViewingSchedule((prev) => (prev?.id === record.id ? null : prev));
      fetchCalendar();
    } catch (e) {
      message.error(e.response?.data?.detail || '删除失败');
    }
  };

  const statusMap = { draft: '草稿', published: '已发布', cancelled: '已取消' };

  const byDay = {};
  calendarEvents.forEach((ev) => {
    const start = dayjs(ev.start_time);
    const end = dayjs(ev.end_time);
    const startDate = start.format('YYYY-MM-DD');
    const endDate = end.format('YYYY-MM-DD');
    for (let d = startDate; d <= endDate; d = dayjs(d).add(1, 'day').format('YYYY-MM-DD')) {
      if (!byDay[d]) byDay[d] = [];
      byDay[d].push(ev);
    }
  });

  const monthStart = dayjs().year(calendarYear).month(calendarMonth - 1).date(1);
  const monthEnd = monthStart.endOf('month');
  const daysInMonth = monthEnd.date();
  const startWeekday = monthStart.day();
  const totalCells = Math.ceil((startWeekday + daysInMonth) / 7) * 7;
  const weekRows = Math.ceil(totalCells / 7);
  // 月视图按周数自适应事件显示行数：周数越少，单格可显示越多
  const maxVisibleEvents = weekRows >= 6 ? 2 : weekRows === 5 ? 3 : 4;
  const cells = [];
  for (let i = 0; i < startWeekday; i++) {
    cells.push({ date: monthStart.subtract(startWeekday - i, 'day'), currentMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: monthStart.date(d), currentMonth: true });
  }
  let next = 1;
  while (cells.length < totalCells) {
    cells.push({ date: monthEnd.add(next, 'day'), currentMonth: false });
    next += 1;
  }

  const firstSelectedId = selectedPersonIds[0] || null;

  // 列表视图：当月内的事件按开始时间排序
  const listEvents = useMemo(() => {
    const start = monthStart.valueOf();
    const end = monthEnd.valueOf();
    return calendarEvents
      .filter((ev) => {
        const t = dayjs(ev.start_time).valueOf();
        return t >= start && t <= end;
      })
      .sort((a, b) => dayjs(a.start_time).valueOf() - dayjs(b.start_time).valueOf());
  }, [calendarEvents, monthStart, monthEnd]);

  return (
    <div
      style={{
        padding: 12,
        background: '#fffbe9',
        flex: 1,
        height: 'calc(100vh - 64px)',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'row',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <style>{`
        .schedule-user-checkbox-wrap .ant-checkbox-inner { border-color: var(--strip-color); }
        .schedule-user-checkbox-wrap .ant-checkbox-checked .ant-checkbox-inner { background: var(--strip-color) !important; border-color: var(--strip-color) !important; }
        .schedule-user-checkbox-wrap .ant-checkbox:hover .ant-checkbox-inner { border-color: var(--strip-color); }
      `}</style>
      {/* 左侧用户列表 */}
      <div
        style={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          marginRight: 12,
          minHeight: 0,
        }}
      >
        <div style={{ marginBottom: 8, flexShrink: 0 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Select
                value={role}
                onChange={setRole}
                options={[
                  { label: '全部', value: 'all' },
                  { label: '老师', value: 'teacher' },
                  { label: '学生', value: 'student' },
                ]}
                style={{ width: 100 }}
              />
              <Button icon={<SettingOutlined />} onClick={() => navigate('/schedules/settings')}>
                设置
              </Button>
            </div>
            <Input.Search
              placeholder="按姓名、用户名或邮箱搜索"
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={() => setSearchValue(searchText)}
              size="small"
            />
            <Space size="small" align="center">
              <Checkbox
                checked={
                  contacts.results.length > 0 &&
                  contacts.results.every((r) => selectedPersonIds.includes(r.id))
                }
                indeterminate={
                  contacts.results.some((r) => selectedPersonIds.includes(r.id)) &&
                  !contacts.results.every((r) => selectedPersonIds.includes(r.id))
                }
                onChange={(e) => (e.target.checked ? selectAllOnPage() : deselectAllOnPage())}
              >
                全选当前页
              </Checkbox>
              <Button type="link" size="small" onClick={clearSelection}>
                清空
              </Button>
            </Space>
          </Space>
        </div>
        <Card
          style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
          styles={{ body: { flex: 1, padding: 8, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
        >
          <Spin spinning={contactsLoading} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <Table
                rowKey="id"
                size="small"
                dataSource={contacts.results}
                pagination={false}
                showHeader={false}
                scroll={{ y: '100%' }}
              columns={[
                {
                  key: 'check',
                  width: 36,
                  render: (_, record) => {
                    const stripColor = getUserStripColor(record.id);
                    return (
                      <span
                        className="schedule-user-checkbox-wrap"
                        style={{ ['--strip-color']: stripColor }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={selectedPersonIds.includes(record.id)}
                          onChange={(e) => onContactSelect(record, e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </span>
                    );
                  },
                },
                {
                  key: 'name',
                  render: (_, record) => (
                    <span style={{ color: '#1890ff' }}>
                      {displayName(record)}
                    </span>
                  ),
                },
              ]}
              onRow={(record) => ({
                onClick: () => {},
              })}
              />
            </div>
          </Spin>
        </Card>
      </div>

      {/* 右侧日历：固定高度 + 绝对定位撑满 */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          height: '100%',
          position: 'relative',
          background: '#fff',
          borderRadius: 8,
          boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03), 0 1px 6px -1px rgba(0,0,0,0.02), 0 2px 4px 0 rgba(0,0,0,0.02)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            padding: '12px 16px',
          }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexShrink: 0 }}>
              <Space wrap>
                <span style={{ fontWeight: 600 }}>{calendarYear} 年 {calendarMonth} 月</span>
                <Button
                  size="small"
                  onClick={() => {
                    if (calendarMonth <= 1) {
                      setCalendarMonth(12);
                      setCalendarYear((y) => y - 1);
                    } else {
                      setCalendarMonth((m) => m - 1);
                    }
                  }}
                >
                  上月
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    if (calendarMonth >= 12) {
                      setCalendarMonth(1);
                      setCalendarYear((y) => y + 1);
                    } else {
                      setCalendarMonth((m) => m + 1);
                    }
                  }}
                >
                  下月
                </Button>
                <Segmented
                  size="small"
                  value={viewMode}
                  onChange={setViewMode}
                  options={[
                    { label: '日历', value: 'calendar' },
                    { label: '列表', value: 'list' },
                  ]}
                />
                <Select
                  size="small"
                  placeholder="显示时区"
                  value={scheduleTz}
                  onChange={(v) => {
                    setScheduleTimezone(v || '');
                    setScheduleTz(v || getScheduleTimezone());
                  }}
                  style={{ width: 160 }}
                  options={SCHEDULE_TIMEZONE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                />
              </Space>
              <Button type="primary" onClick={() => setCreateModalOpen(true)}>
                新建排课
              </Button>
            </div>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
                {viewMode === 'list' ? (
                <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  <Table
                    rowKey={(r) => `${r.id}-${r.start_time}`}
                    size="small"
                    dataSource={listEvents}
                    pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `共 ${t} 条` }}
                    scroll={{ x: 800 }}
                    tableLayout="fixed"
                    columns={[
                      {
                        title: '日期',
                        dataIndex: 'start_time',
                        key: 'date',
                        width: 72,
                        render: (v) => formatInTz(v, 'MM-DD'),
                      },
                      {
                        title: '开始',
                        dataIndex: 'start_time',
                        key: 'start',
                        width: 56,
                        render: (v) => formatInTz(v, 'HH:mm'),
                      },
                      {
                        title: '结束',
                        dataIndex: 'end_time',
                        key: 'end',
                        width: 56,
                        render: (v) => formatInTz(v, 'HH:mm'),
                      },
                      {
                        title: '课程',
                        key: 'course',
                        width: 220,
                        ellipsis: true,
                        render: (_, ev) => formatEventTextMinimal(ev, firstSelectedId, scheduleTz),
                      },
                      {
                        title: '老师',
                        dataIndex: 'teacher_name',
                        key: 'teacher',
                        width: 88,
                        ellipsis: true,
                        render: (v) => v || '—',
                      },
                      {
                        title: '学生',
                        key: 'students',
                        width: 110,
                        ellipsis: true,
                        render: (_, ev) => {
                          const names = (ev.participants || [])
                            .filter((p) => p.role === 'student')
                            .map((p) => p.user_real_name || p.user_name || '')
                            .filter(Boolean);
                          return names.length ? names.join('、') : '—';
                        },
                      },
                      {
                        title: '操作',
                        key: 'action',
                        width: 180,
                        render: (_, ev) => (
                          <Space size="small" onClick={(e) => e.stopPropagation()}>
                            <Button type="link" size="small" onClick={() => openView(ev)}>
                              查看
                            </Button>
                            <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => copyLaunchUrl(ev.id)}>
                              复制链接
                            </Button>
                            <Button
                              type="link"
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() =>
                                Modal.confirm({
                                  title: '确认删除',
                                  content: `确定要删除排课「${ev.course_name || '未命名'}」吗？`,
                                  okText: '删除',
                                  okType: 'danger',
                                  cancelText: '取消',
                                  onOk: () => deleteSchedule(ev),
                                })
                              }
                            >
                              删除
                            </Button>
                          </Space>
                        ),
                      },
                    ]}
                    onRow={(record) => ({
                      onClick: (e) => {
                        if (e.target.closest('button')) return;
                        openView(record);
                      },
                      style: { cursor: 'pointer' },
                    })}
                  />
                </div>
              ) : (
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      gridTemplateRows: `auto repeat(${Math.ceil(totalCells / 7)}, 1fr)`,
                      gap: 1,
                      border: '1px solid #eee',
                      flex: 1,
                      minHeight: 0,
                    }}
                  >
                {['日', '一', '二', '三', '四', '五', '六'].map((h) => (
                  <div
                    key={h}
                    style={{
                      padding: 8,
                      background: '#fafafa',
                      fontWeight: 600,
                      textAlign: 'center',
                    }}
                  >
                    {h}
                  </div>
                ))}
                {cells.map((c, i) => {
                  const key = c.date.format('YYYY-MM-DD');
                  const events = byDay[key] || [];
                  const localDate = new Date(c.date.year(), c.date.month(), c.date.date());
                  const lunarStr = getLunarDate(localDate);
                  const holidayStr = getHolidayName(localDate, lunarStr);
                  return (
                    <div
                      key={i}
                      style={{
                        padding: 6,
                        background: c.currentMonth ? '#fff' : '#fafafa',
                        border: '1px solid #eee',
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                      }}
                    >
                      <div style={{ flexShrink: 0, marginBottom: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ flex: 1 }}>
                          <span style={{ color: c.currentMonth ? '#333' : '#999', fontSize: 14, fontWeight: 500 }}>
                            {c.date.date()}
                          </span>
                          {lunarStr && (
                            <div style={{ fontSize: 9, color: c.currentMonth ? '#666' : '#999', lineHeight: 1.1, marginTop: 1 }}>{lunarStr}</div>
                          )}
                          {holidayStr && (
                            <div style={{ fontSize: 9, color: '#c41e3a', fontWeight: 500, lineHeight: 1.1, marginTop: 1 }}>{holidayStr}</div>
                          )}
                        </div>
                        {events.length > 0 && (
                          <div
                            style={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              background: '#1890ff',
                              color: '#fff',
                              fontSize: 10,
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              marginLeft: 4,
                            }}
                          >
                            {events.length}
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
                        {events.slice(0, maxVisibleEvents).map((ev) => (
                          <div
                            key={`${ev.id}-${key}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => openView(ev)}
                            onKeyDown={(e) => e.key === 'Enter' && openView(ev)}
                            title={formatEventText(ev, firstSelectedId, scheduleTz)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              fontSize: 12,
                              lineHeight: 1.5,
                              marginTop: 4,
                              padding: '2px 4px',
                              borderRadius: 4,
                              cursor: 'pointer',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#f5f5f5';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                            }}
                          >
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                flexShrink: 0,
                                backgroundColor: getEventDotColor(ev, selectedPersonIds),
                              }}
                              aria-hidden
                            />
                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {formatEventTextMinimal(ev, firstSelectedId, scheduleTz)}
                            </span>
                          </div>
                        ))}
                        {events.length > maxVisibleEvents && (
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => setDayEventsModal({ open: true, date: key, events })}
                            onKeyDown={(e) => e.key === 'Enter' && setDayEventsModal({ open: true, date: key, events })}
                            style={{
                              fontSize: 12,
                              marginTop: 4,
                              padding: '2px 4px',
                              borderRadius: 4,
                              cursor: 'pointer',
                              textAlign: 'center',
                              color: '#1890ff',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#f5f5f5';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                            }}
                          >
                            另外 {events.length - maxVisibleEvents} 项
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                  </div>
                </div>
              )}
              {calendarLoading && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255,255,255,0.45)',
                    zIndex: 2,
                  }}
                >
                  <Spin />
                </div>
              )}
            </div>
        </div>
      </div>

      {/* 排课详情 Drawer */}
      <Drawer
        title={viewingSchedule?.course_name ?? '课程详情'}
        open={!!viewingSchedule}
        onClose={() => setViewingSchedule(null)}
        width={480}
      >
        {viewingSchedule?._loading || viewingScheduleLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : viewingSchedule && viewingSchedule.id ? (
          <Descriptions column={1} bordered size="small" labelStyle={{ width: 120, background: '#fafafa' }}>
            <Descriptions.Item label="课程名">{viewingSchedule.course_name}</Descriptions.Item>
            <Descriptions.Item label="状态">{statusMap[viewingSchedule.status] ?? viewingSchedule.status}</Descriptions.Item>
            <Descriptions.Item label="排课方式">
              {Array.isArray(viewingSchedule.class_dates) && viewingSchedule.class_dates.length > 0 && viewingSchedule.time_slot_start != null
                ? '按日期（每天时间段 + 上课日期）'
                : '单次（指定开始/结束时间）'}
            </Descriptions.Item>
            {Array.isArray(viewingSchedule.class_dates) && viewingSchedule.class_dates.length > 0 && viewingSchedule.time_slot_start != null ? (
              <>
                <Descriptions.Item label="每日时间">
                  {viewingSchedule.time_slot_start && viewingSchedule.time_slot_end
                    ? `${String(viewingSchedule.time_slot_start).slice(0, 5)} — ${String(viewingSchedule.time_slot_end).slice(0, 5)}`
                    : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="上课日期">
                  {(viewingSchedule.class_dates || []).length
                    ? (viewingSchedule.class_dates || []).slice(0, 10).join('、') + ((viewingSchedule.class_dates || []).length > 10 ? ' …' : '')
                    : '—'}
                </Descriptions.Item>
              </>
            ) : (
              <>
                <Descriptions.Item label="开始时间">
                  {viewingSchedule.start_time ? formatInTz(viewingSchedule.start_time, 'YYYY-MM-DD HH:mm') : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="结束时间">
                  {viewingSchedule.end_time ? formatInTz(viewingSchedule.end_time, 'YYYY-MM-DD HH:mm') : '—'}
                </Descriptions.Item>
              </>
            )}
            <Descriptions.Item label="老师">{viewingSchedule.teacher_name ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="学生">
              {(viewingSchedule.participants || [])
                .filter((p) => p.role === 'student')
                .map((p) => p.user_name)
                .join('、') || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="提醒提前">
              {(viewingSchedule.reminder_lead_minutes || []).length ? (viewingSchedule.reminder_lead_minutes || []).join('、') + ' 分钟' : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="提醒渠道">
              {(viewingSchedule.reminder_channels || []).length ? (viewingSchedule.reminder_channels || []).join('、') : '—'}
            </Descriptions.Item>
            {viewingSchedule.wecom_contact_sender_userid && (
              <Descriptions.Item label="企微发送者">{viewingSchedule.wecom_contact_sender_userid}</Descriptions.Item>
            )}
            <Descriptions.Item label="Meet 链接或会议 ID">
              {viewingSchedule.meet_link || '—'}
              {!isRealMeetLink(viewingSchedule.meet_link) && (
                <Button
                  type="primary"
                  size="small"
                  loading={createMeetLoading}
                  style={{ marginLeft: 8 }}
                  onClick={() => handleCreateMeet(viewingSchedule.id)}
                >
                  创建 Meet 链接
                </Button>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="FrameVR 场景">{viewingSchedule.frame_slug || '—'}</Descriptions.Item>
            <Descriptions.Item label="一键启动">
              <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => copyLaunchUrl(viewingSchedule.id)}>
                复制启动链接
              </Button>
            </Descriptions.Item>
            <Descriptions.Item label="通知历史" span={1}>
              <NotificationHistory scheduleId={viewingSchedule.id} />
            </Descriptions.Item>
          </Descriptions>
        ) : null}
      </Drawer>

      {/* 某日多事件弹框：类似 Google 日历，周X + 日期 + 可滚动列表 */}
      {dayEventsModal.open && dayEventsModal.date && (
        <Modal
          open={dayEventsModal.open}
          onCancel={() => setDayEventsModal({ open: false, date: null, events: [] })}
          footer={null}
          width={360}
          styles={{ body: { paddingTop: 8, maxHeight: '70vh', overflowY: 'auto' } }}
          closable
          title={null}
        >
          <div style={{ marginBottom: 16, paddingRight: 24 }}>
            <div style={{ fontSize: 14, color: '#666' }}>
              周{['日', '一', '二', '三', '四', '五', '六'][dayjs(dayEventsModal.date).day()]}
            </div>
            <div style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.2 }}>
              {dayjs(dayEventsModal.date).date()}
            </div>
          </div>
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            {(dayEventsModal.events || []).map((ev) => (
              <div
                key={`${ev.id}-${ev.start_time}`}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setDayEventsModal({ open: false, date: null, events: [] });
                  openView(ev);
                }}
                onKeyDown={(e) => e.key === 'Enter' && (setDayEventsModal({ open: false, date: null, events: [] }), openView(ev))}
                title={formatEventText(ev, firstSelectedId, scheduleTz)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 10,
                  padding: '8px 0',
                  cursor: 'pointer',
                  borderRadius: 4,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    flexShrink: 0,
                    backgroundColor: getEventDotColor(ev, selectedPersonIds),
                  }}
                  aria-hidden
                />
                <span style={{ flex: 1, minWidth: 0 }}>
                  {formatEventTextMinimal(ev, firstSelectedId, scheduleTz)}
                </span>
              </div>
            ))}
          </div>
        </Modal>
      )}

      <ScheduleFormModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        editingId={null}
        onSuccess={() => fetchCalendar()}
      />
    </div>
  );
};

export default ScheduleCalendarPage;
